/**
 * Editor pay (spec #196): the portal computes this itself now - no more
 * link-out, no more proxy to the old Hub billing engine. Mounted inside
 * `EditorScreen`'s `HubPage` (the dashboard's header and background) while
 * open, never threading its data through the dashboard's own fetch.
 *
 * Drawn like the hub's billing page (Shawn 2026-11-01): month pills, the
 * invoice as light paper on the dark desk, net earnings up top, one row per
 * line with its appeal underneath. The hub rules come from the generated part
 * of `editor-hub.css` (mockup `docs/design/editor-dashboard-2026-11/billing.html`).
 *
 * The current month is `status: "live"` - computed on the fly, nothing
 * written yet. Any other period is either a closed ledger invoice
 * (draft/approved/paid) or, for a pre-cutover month, a read-only `legacy`
 * row with no line detail. A claim can only be filed once there is a real
 * invoice (draft or approved) - never against a live or legacy period.
 *
 * @module web/screens/EditorBilling
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, get, post } from "../api/client.ts";
import type { PayClaimResponse, PayInvoiceFullResponse, PayInvoiceStatus, PayLine, PayPeriodsResponse } from "../api/types.ts";
import { HubPageHead } from "./EditorHub.tsx";
import { useI18n, type I18n, type MessageKey } from "../i18n/index.tsx";

const MAX_TEXT_LENGTH = 1000;

/** Last-resort fallback, only when `/api/editor/billing/periods` itself is unreachable. */
function localFallbackPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function periodLabel(period: string, date: I18n["date"]): string {
  const [y, m] = period.split("-");
  if (!y || !m) return period;
  return date(new Date(Number(y), Number(m) - 1, 1), { month: "long", year: "numeric" });
}

const LINE_LABEL_KEYS: Record<PayLine["type"], MessageKey> = {
  delivery: "billing.line_delivery",
  needs_rate: "billing.line_needs_rate",
  mentor: "billing.line_mentor",
  late_deduction: "billing.line_late",
  gen_own_cost: "billing.line_gen_own_cost",
  adjustment: "billing.line_adjustment",
};

function lineLabel(type: PayLine["type"], t: I18n["t"]): string {
  const key = LINE_LABEL_KEYS[type];
  return key ? t(key) : type;
}

/**
 * The ledger stores English labels with the line type baked in ("Mentor bonus: Card", "Late: Card",
 * "gen Own Cost"; payroll-engine.ts). The screen shows the translated type itself, so drop that part.
 */
function lineSubject(l: Pick<PayLine, "type" | "label">): string {
  if (l.type === "gen_own_cost") return "";
  return l.label.replace(/^(Mentor bonus|Late):\s*/, "");
}

/** The hub's status pill on the invoice paper. */
const BADGE_KEYS: Record<PayInvoiceStatus, MessageKey> = {
  live: "billing.badge_live",
  draft: "billing.badge_draft",
  approved: "billing.badge_approved",
  paid: "billing.badge_paid",
  legacy: "billing.badge_legacy",
};

/** Claim status from the ledger; an unknown value is shown as the server sent it. */
const CLAIM_STATUS_KEYS: Partial<Record<string, MessageKey>> = {
  open: "billing.claim_open",
  accepted: "billing.claim_accepted",
  rejected: "billing.claim_rejected",
};

export function EditorBilling({ onBack, me }: { onBack: () => void; me?: { displayName: string; email?: string | null } }) {
  const { t, eur, date } = useI18n();
  const [periods, setPeriods] = useState<string[] | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const [data, setData] = useState<PayInvoiceFullResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimTarget, setClaimTarget] = useState<{ workItemId: string | null; label: string } | null>(null);
  const [received, setReceived] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const userChangedPeriod = useRef(false);

  useEffect(() => {
    // The server's reported current period (its first entry, see
    // `PayPeriodsResponse`) is the only source of truth for "this month" -
    // the browser's own clock can disagree with Europe/Berlin and with the
    // 2026-11 cutover clamp.
    void get<PayPeriodsResponse>("/api/editor/billing/periods")
      .then((r) => {
        setPeriods(r.periods);
        if (!userChangedPeriod.current) setPeriod((prev) => prev ?? r.periods[0] ?? null);
      })
      .catch(() => {
        setPeriods([]);
        if (!userChangedPeriod.current) setPeriod((prev) => prev ?? localFallbackPeriod());
      });
  }, []);

  const load = useCallback(async () => {
    if (period === null) return;
    setLoading(true);
    setError(null);
    try {
      const body = await get<PayInvoiceFullResponse>(`/api/editor/billing?period=${encodeURIComponent(period)}`);
      setData(body);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 503 ? t("billing.err_unavailable") : t("billing.err_load"));
    } finally {
      setLoading(false);
    }
  }, [period, t]);

  useEffect(() => {
    void load();
    setReceived({ busy: false, error: null });
  }, [load]);

  const claimable = data?.invoice.status === "draft" || data?.invoice.status === "approved";
  const deliveryLines = useMemo(() => (data?.invoice.lines ?? []).filter((l) => l.type === "delivery" || l.type === "needs_rate"), [data]);
  const otherLines = useMemo(() => (data?.invoice.lines ?? []).filter((l) => l.type !== "delivery" && l.type !== "needs_rate"), [data]);
  const displayPeriod = period ?? localFallbackPeriod();

  async function markReceived(): Promise<void> {
    if (!data || !period) return;
    setReceived({ busy: true, error: null });
    try {
      await post("/api/editor/billing/mark-received", { period, claimedTotalCents: data.invoice.totalCents });
      setReceived({ busy: false, error: null });
      void load();
    } catch (err) {
      setReceived({ busy: false, error: err instanceof ApiError ? err.message : t("billing.err_mark") });
    }
  }

  const inv = data?.invoice;
  const cardCount = deliveryLines.length;
  const periodList = periods && periods.length ? periods : [displayPeriod];

  return (
    <div className="hd-page billing-view" id="billing-view" data-screen="editor-billing">
      <div className="billing-content">
        <HubPageHead title={t("billing.title")} backLabel={t("billing.back")} onBack={onBack} />

        {periodList.length > 1 && (
          <div className="billing-months" role="tablist" aria-label={t("billing.period_label")}>
            {periodList.map((p) => (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={p === displayPeriod}
                className={`billing-month-pill${p === displayPeriod ? " active" : ""}`}
                onClick={() => { userChangedPeriod.current = true; setPeriod(p); }}
              >
                {periodLabel(p, date)}
              </button>
            ))}
          </div>
        )}

        {error && <p className="bill-banner bill-banner-line hd-billing-error" role="alert">{error}</p>}
        {loading && <div className="bill-loading glass-card">{t("billing.loading")}</div>}

        {!loading && data && inv && (
          <>
            {claimable && (
              <div className="bill-banner">
                {inv.claimedTotalCents === null ? (
                  <div className="bill-actions">
                    <button className="btn-orange btn-sm" disabled={received.busy} onClick={() => void markReceived()}>
                      {received.busy ? t("billing.marking") : t("billing.mark_sent")}
                    </button>
                  </div>
                ) : (
                  <div className="bill-banner-sub">{t("billing.invoice_sent", { amount: eur(inv.claimedTotalCents) })}</div>
                )}
                {received.error && <div className="bill-banner-sub hd-billing-error" role="alert">{received.error}</div>}
              </div>
            )}

            <div className="invoice-card">
              <div className="invoice-header">
                <div>
                  <div className="invoice-title">{t("billing.invoice_title", { period: periodLabel(displayPeriod, date) })}</div>
                  {me && <div className="invoice-billed-to">{me.displayName}{me.email ? ` · ${me.email}` : ""}</div>}
                  <div className="invoice-meta">{cardCount === 1 ? t("billing.cards_one") : t("billing.cards_many", { n: cardCount })}</div>
                  {inv.hasNeedsRate && <div className="invoice-note invoice-state-review">{t("billing.needs_rate_warning")}</div>}
                </div>
                <span className={`invoice-status-badge invoice-status-${inv.status}`}>{t(BADGE_KEYS[inv.status])}</span>
              </div>

              <div className="billing-net-summary"><span>{t("billing.net_earnings")}</span><strong>{eur(inv.totalCents)}</strong></div>

              <table className="invoice-table">
                <thead>
                  <tr><th>{t("billing.col_card")}</th><th className="invoice-amount">{t("billing.col_amount")}</th></tr>
                </thead>
                <tbody>
                  {deliveryLines.map((l, i) => (
                    <tr key={`${l.workItemId ?? i}-${l.type}`}>
                      <td>
                        <div className="invoice-card-name">{l.label}</div>
                        {claimable && (
                          <div className="invoice-appeal-row">
                            <button type="button" className="invoice-appeal" onClick={() => setClaimTarget({ workItemId: l.workItemId, label: l.label })}>{t("billing.appeal")}</button>
                          </div>
                        )}
                      </td>
                      <td className="invoice-amount">
                        {eur(l.amountCents)}
                        {l.type === "needs_rate" && <div><span className="invoice-state invoice-state-review">{t("billing.needs_rate_pill")}</span></div>}
                      </td>
                    </tr>
                  ))}
                  {otherLines.map((l, i) => (
                    <tr key={`other-${l.workItemId ?? i}-${l.type}`}>
                      <td>
                        <div className="invoice-card-name">{lineLabel(l.type, t)}</div>
                        {lineSubject(l) && <div className="invoice-adj">{lineSubject(l)}</div>}
                      </td>
                      <td className="invoice-amount">{eur(l.amountCents)}</td>
                    </tr>
                  ))}
                  {deliveryLines.length === 0 && otherLines.length === 0 && (
                    <tr><td colSpan={2} className="invoice-note">{t("billing.no_cards")}</td></tr>
                  )}
                  {claimable && (
                    <tr className="invoice-row-disputed">
                      <td colSpan={2}>
                        <button type="button" className="invoice-appeal invoice-appeal-whole" onClick={() => setClaimTarget({ workItemId: null, label: t("billing.whole_invoice") })}>
                          {t("billing.appeal_whole")}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="invoice-total"><span>{t("billing.total")}</span><span className="invoice-total-amount">{eur(inv.totalCents)}</span></div>

              {data.claims.length > 0 && (
                <div className="invoice-claims">
                  <div className="invoice-meta">{t("billing.your_claims")}</div>
                  {data.claims.map((c) => (
                    <div key={c.id} className="invoice-note">
                      {c.text} · <strong>{CLAIM_STATUS_KEYS[c.status] ? t(CLAIM_STATUS_KEYS[c.status]!) : c.status}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {inv.status === "live" && <div className="bill-window">{t("billing.status_live")}</div>}
            {inv.status === "legacy" && <div className="bill-window">{t("billing.status_legacy")}</div>}
          </>
        )}

        {claimTarget && (
          <ClaimDialog
            period={displayPeriod}
            target={claimTarget}
            onClose={() => setClaimTarget(null)}
            onFiled={() => { setClaimTarget(null); void load(); }}
          />
        )}
      </div>
    </div>
  );
}

/** The ledger refuses a claim without proof - only an http(s) link counts. */
function isProofLink(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function ClaimDialog({ period, target, onClose, onFiled }: { period: string; target: { workItemId: string | null; label: string }; onClose: () => void; onFiled: () => void }) {
  const [text, setText] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const proofOk = isProofLink(proofUrl);
  const proofInvalid = proofUrl.trim() !== "" && !proofOk;

  async function submit() {
    if (!text.trim() || !proofOk || busy) return;
    setBusy(true);
    setError(null);
    try {
      await post<PayClaimResponse>("/api/editor/billing/claim", {
        period,
        workItemId: target.workItemId,
        text: text.trim().slice(0, MAX_TEXT_LENGTH),
        proofUrl: proofUrl.trim(),
      });
      onFiled();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("billing.err_claim"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ed-modal-backdrop" role="dialog" aria-modal="true" aria-label={t("billing.appeal_target", { target: target.label })}>
      <div className="ed-modal ed-card">
        <h3>{t("billing.appeal_target", { target: target.label })}</h3>
        <textarea placeholder={t("billing.claim_text_ph")} value={text} onChange={(e) => setText(e.target.value)} maxLength={MAX_TEXT_LENGTH} autoFocus />
        <input type="url" required placeholder={t("billing.proof_ph")} aria-label={t("billing.proof_ph")} aria-invalid={proofInvalid} value={proofUrl} onChange={(e) => setProofUrl(e.target.value)} />
        {proofInvalid && <p className="editor-offer-error">{t("billing.proof_hint")}</p>}
        {error && <p className="editor-offer-error">{error}</p>}
        <div className="ed-submit-actions">
          <button className="ed-primary" disabled={busy || !text.trim() || !proofOk} onClick={() => void submit()}>
            {busy ? t("billing.filing") : t("billing.file_claim")}
          </button>
          <button className="ed-secondary" disabled={busy} onClick={onClose}>{t("billing.cancel")}</button>
        </div>
      </div>
    </div>
  );
}
